'use client';
import { useCallback, useEffect, useRef } from 'react';

import annalsRetention from '~features/annals/hooks/retention';
import { useEmailConfirmModal } from '~features/emailConfirm/hooks/useEmailConfirmModal';
import { useModal } from '~hooks/useModal';
import { useSession } from '~hooks/useSession';
import { usePathname } from '~i18n/navigation';
import ClientLogger from '~logger/client';
import { LoggerMessages, LoggerServices } from '~logger/constants';
import { userApi } from '~services/api/user';
import { retentionApi } from '~shared/api/retention';
import type { RetentionPopupResponse } from '~shared/api/retention/types';

import { detectIfBannerCtaClicked } from '../../utils/retention-popup-url-parser';
import { useHashListener } from '../../utils/use-hash-listener';
import { BannerModal } from '../BannerModal';

export const NewsBanner = () => {
	const { userId } = useSession();
	const { isOpen, open, close: closeModal } = useModal();
	const { isOpened: isEmailModalOpened } = useEmailConfirmModal();
	const pathname = usePathname();
	const pathnameRef = useRef<string>(pathname);
	const dataRef = useRef<RetentionPopupResponse>(undefined);

	const { data: userConfigData } = userApi.useUserConfigQuery(
		{ userId },
		{ skip: !userId }
	);

	const { data, isSuccess } = retentionApi.usePopupConfigQuery(
		{ userId, jwt: userConfigData?.retentionJwt as string },
		{ skip: !userConfigData?.retentionJwt }
	);

	useEffect(() => {
		dataRef.current = data;
	}, [data]);

	const popupShownAnalytics = annalsRetention.useRetentionPopupShown();
	const popupCTAClickedAnalytics =
		annalsRetention.useRetentionPopupCTAClicked();

	const [removeRetentionPopup] = retentionApi.useDeleteConfigMutation();

	useEffect(() => {
		if (
			isSuccess &&
			data.content &&
			!isEmailModalOpened &&
			!detectIfBannerCtaClicked()
		) {
			open()();
			popupShownAnalytics({
				messageId: data.messageId,
				eventId: data.event,
			});
		}
	}, [isEmailModalOpened, open, isSuccess, data, popupShownAnalytics]);

	const onClose = useCallback(() => {
		if (!userConfigData?.retentionJwt) {
			closeModal();
			return;
		}
		void removeRetentionPopup({ userId, jwt: userConfigData.retentionJwt });
		closeModal();
	}, [closeModal, removeRetentionPopup, userConfigData?.retentionJwt, userId]);

	const onCTAClicked = useCallback(() => {
		if (data) {
			popupCTAClickedAnalytics({
				messageId: data.messageId,
				eventId: data.event,
				pathname: pathnameRef.current,
			});
		} else {
			ClientLogger.error(
				LoggerServices.RetentionService,
				LoggerMessages.NoDataOnRetentionPopup,
				{}
			);
		}

		onClose();
	}, [data, onClose, popupCTAClickedAnalytics]);

	useHashListener(onCTAClicked, !!userId && !!data);

	if (!data?.content) {
		return;
	}

	return (
		<BannerModal
			content={data.content}
			open={isOpen}
			locked={true}
			onClose={onClose}
		/>
	);
};
